import { useEffect, useRef, useState } from "react";
import { ChevronDown, Check } from "lucide-react";
import api from "@/lib/api";

const OTA = ["makemytrip", "goibibo", "booking.com", "agoda", "airbnb", "expedia", "yatra", "easemytrip", "oyo"];
export const isOtaSource = (s) => { const n = (s || "").trim().toLowerCase(); return OTA.includes(n) || n.includes("ota"); };

let cache = null;
export async function fetchReservationSources() {
  if (cache) return cache;
  const { data } = await api.get("/documents/reservation-sources");
  cache = data.sources || [];
  return cache;
}
export const invalidateReservationSources = () => { cache = null; };

const TONES = {
  dark: { label: "text-xs uppercase tracking-wider text-hocp-textMuted", input: "bg-hocp-bgCard border border-hocp-border rounded-md h-10 text-[14px]" },
  light: { label: "text-[12px] uppercase tracking-wider text-slate-500 font-semibold", input: "bg-white border-2 border-hocp-border rounded-lg h-12 text-[14px]" },
};

/**
 * ReservationSourceInput — shared combobox (type-to-search or pick a suggestion; free text allowed).
 * value = { reservation_source, reservation_source_ref } · shows OTA Booking Reference when an OTA is chosen.
 */
export function ReservationSourceInput({ value = {}, onChange, tone = "dark", testidPrefix = "res-source" }) {
  const t = TONES[tone] || TONES.dark;
  const [sources, setSources] = useState([]);
  const [open, setOpen] = useState(false);
  const [active, setActive] = useState(-1);
  const box = useRef(null);
  useEffect(() => { fetchReservationSources().then(setSources).catch(() => setSources([])); }, []);
  useEffect(() => {
    const onDoc = (e) => { if (box.current && !box.current.contains(e.target)) setOpen(false); };
    document.addEventListener("mousedown", onDoc);
    return () => document.removeEventListener("mousedown", onDoc);
  }, []);

  const src = value.reservation_source || "";
  const q = src.trim().toLowerCase();
  const matches = q ? sources.filter((s) => s.toLowerCase().includes(q)) : sources;
  const exact = sources.some((s) => s.toLowerCase() === q);
  const pick = (s) => { onChange({ ...value, reservation_source: s }); setOpen(false); setActive(-1); };
  const onKey = (e) => {
    if (!open && ["ArrowDown", "ArrowUp"].includes(e.key)) { setOpen(true); return; }
    if (e.key === "ArrowDown") { e.preventDefault(); setActive((a) => Math.min(a + 1, matches.length - 1)); }
    else if (e.key === "ArrowUp") { e.preventDefault(); setActive((a) => Math.max(a - 1, 0)); }
    else if (e.key === "Enter" && open && active >= 0 && matches[active]) { e.preventDefault(); pick(matches[active]); }
    else if (e.key === "Escape") setOpen(false);
  };

  return (
    <div className="space-y-3" data-testid={`${testidPrefix}-block`}>
      <div ref={box} className="relative">
        <label className={t.label}>Reservation Source <span className="normal-case tracking-normal font-normal opacity-70">(optional)</span></label>
        <div className="relative mt-1">
          <input value={src} maxLength={100} placeholder="Type or pick — e.g. MakeMyTrip, Corporate…" role="combobox" aria-expanded={open} aria-autocomplete="list"
            onChange={(e) => { onChange({ ...value, reservation_source: e.target.value }); setOpen(true); setActive(-1); }}
            onFocus={() => setOpen(true)} onKeyDown={onKey}
            className={`${t.input} w-full px-3 pr-10 outline-none focus:border-hocp-mint focus:shadow-[0_0_0_3px_rgba(55,244,181,0.15)]`} data-testid={`${testidPrefix}-input`} />
          <button type="button" onClick={() => setOpen((o) => !o)} className="absolute right-0 top-0 h-full w-10 flex items-center justify-center text-hocp-textMuted" aria-label="Show sources" data-testid={`${testidPrefix}-toggle`}>
            <ChevronDown size={18} strokeWidth={1.5} className={`transition-transform ${open ? "rotate-180" : ""}`} />
          </button>
        </div>
        {open && (matches.length > 0 || (q && !exact)) && (
          <ul role="listbox" className="absolute z-50 mt-1 w-full max-h-56 overflow-y-auto bg-white border border-hocp-border rounded-md shadow-lg py-1 text-[14px]" data-testid={`${testidPrefix}-list`}>
            {matches.map((s, i) => {
              const sel = s.toLowerCase() === q;
              return (
                <li key={s} role="option" aria-selected={sel} onMouseDown={(e) => { e.preventDefault(); pick(s); }} onMouseEnter={() => setActive(i)}
                  className={`px-3 min-h-[40px] flex items-center justify-between cursor-pointer ${sel ? "bg-hocp-mintSoft text-hocp-deepTeal font-medium" : i === active ? "bg-hocp-ghost text-hocp-deepTeal" : "text-hocp-textPrimary hover:bg-hocp-ghost hover:text-hocp-deepTeal"}`}
                  data-testid={`${testidPrefix}-option-${s.replace(/[^a-z0-9]+/gi, "-").toLowerCase()}`}>
                  {s}{sel && <Check size={16} strokeWidth={2} />}
                </li>);
            })}
            {q && !exact && <li className="px-3 py-2 text-[12px] text-hocp-textMuted border-t border-hocp-borderSubtle" data-testid={`${testidPrefix}-freetext-hint`}>Use “{src.trim()}” as a custom source</li>}
          </ul>
        )}
      </div>
      {isOtaSource(src) && (
        <div data-testid={`${testidPrefix}-ref-block`}>
          <label className={t.label}>OTA Booking Reference</label>
          <input value={value.reservation_source_ref || ""} maxLength={100} placeholder="e.g. MMT-98234761"
            onChange={(e) => onChange({ ...value, reservation_source_ref: e.target.value })}
            className={`${t.input} w-full px-3 mt-1 mono outline-none focus:border-hocp-mint focus:shadow-[0_0_0_3px_rgba(55,244,181,0.15)]`} data-testid={`${testidPrefix}-ref`} />
        </div>
      )}
    </div>
  );
}
